"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type FormEvent } from "react";
import { BarChart3, BookOpen, Menu, Search, Settings, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BillingTab,
  CustomerBand,
  CustomerTabs,
  InsightRail,
  InteractionsTab,
  OverviewTab,
  ServicesTab,
  TicketsTab,
  VerifyBanner,
} from "@/components/customer/customer-360";
import { OneViewLegend, OneViewMark } from "@/components/desktop/api-source";
import { AdjustModal, TicketModal, VerifyModal } from "@/components/desktop/modals";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdminView, BusinessView, EmptySearch, ForbiddenCustomer, IdleSearch, QueueView, ReportsView } from "@/components/views/shells";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ROLE_OPTIONS } from "@/lib/auth/roles";
import { api, toastCopy } from "@/lib/client-api";
import { MARIA_ID } from "@/lib/fixtures/maria";
import { resolveHostAccountSearchQuery } from "@/iframe/hostAccount";
import type { Customer360, JournalEvent, SessionResponse, UsageLine } from "@/lib/types";
import { observeAndReportSize } from "@/iframe/resize";
import { useIframeSession } from "@/iframe/session";

type View = "customer" | "queue" | "reports" | "business" | "admin";
type Tab = "overview" | "billing" | "services" | "interactions" | "tickets";

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

export function AgentDesktop() {
  const iframeSession = useIframeSession();
  const layoutRef = useRef<HTMLDivElement>(null);
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [customer, setCustomer] = useState<Customer360 | null>(null);
  const [events, setEvents] = useState<JournalEvent[]>([]);
  const [view, setView] = useState<View>("customer");
  const [tab, setTab] = useState<Tab>("overview");
  const [query, setQuery] = useState("");
  const [searchEmpty, setSearchEmpty] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [booted, setBooted] = useState(false);
  const [useMariaDemo, setUseMariaDemo] = useState(true);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentRows, setConsentRows] = useState<{ effective: string; channel: string; serviceDataForOffers: string }[]>([]);
  const [usageLines, setUsageLines] = useState<UsageLine[] | null>(null);

  const perms = session?.perms;
  const customerId = customer?.id;

  const refreshJournal = useCallback(async () => {
    const data = await api.journal();
    setEvents(data.events);
  }, []);

  const journal = useCallback(
    async (action: string, summary?: string, silent = false, extra: Record<string, string | number | boolean | null> = {}) => {
      if (!customerId) return;
      const event = await api.writeJournal(action, customerId, summary ? { summary, ...extra } : extra);
      setEvents((prev) => [event, ...prev]);
      if (!silent) toast(toastCopy(action, event.detail));
      return event;
    },
    [customerId],
  );

  const loadCustomer = useCallback(async (id: string) => {
    try {
      const data = await api.customer360(id);
      setCustomer(data);
      setSearchEmpty(false);
      setLoadError(null);
      return data;
    } catch (error) {
      const status = (error as Error & { status?: number }).status;
      setCustomer(null);
      setUsageLines(null);
      if (status === 404) {
        setSearchEmpty(true);
        setLoadError(null);
        return null;
      }
      setSearchEmpty(false);
      setLoadError(error instanceof Error ? error.message : "Unable to load that account.");
      return null;
    }
  }, []);

  const bootedForStatus = useRef<string | null>(null);

  useEffect(() => {
    if (!layoutRef.current) return;
    return observeAndReportSize(layoutRef.current);
  }, []);

  useEffect(() => {
    if (iframeSession.status === "waitingHello") return;
    const bootKey = iframeSession.status;
    if (bootedForStatus.current === bootKey) return;
    bootedForStatus.current = bootKey;

    if (iframeSession.status === "denied") {
      setLoadError("Host origin is not on the allowlist. Embedded session refused.");
      setBooted(true);
      return;
    }

    (async () => {
      try {
        const [next, runtime] = await Promise.all([api.session(), api.config()]);
        const mariaDemo = runtime.bffUpstream === "mock";
        setUseMariaDemo(mariaDemo);
        setSession(next);
        await refreshJournal();
        if (next.perms.customer) {
          const hostQuery =
            iframeSession.status === "ready"
              ? resolveHostAccountSearchQuery(iframeSession.sessionContext)
              : null;

          if (hostQuery) {
            setQuery(hostQuery);
            const result = await api.search(hostQuery);
            if (result.results[0]) {
              const loaded = await loadCustomer(result.results[0].id);
              if (loaded) {
                await api.writeJournal("customer360.opened", loaded.id, {
                  summary: "Customer 360 opened from OneAgent account context",
                });
                await refreshJournal();
              }
            } else {
              setSearchEmpty(true);
              setCustomer(null);
            }
          } else if (mariaDemo) {
            const loaded = await loadCustomer(MARIA_ID);
            if (loaded) {
              await api.writeJournal("interaction.started", MARIA_ID, {
                summary: "Voice - Maria Delgado - acct 00447-190288-15 - screen-pop with predicted contact reasons",
              });
              await api.writeJournal("customer360.opened", MARIA_ID, {
                summary: "Overview tab - protected fields masked pending verification",
              });
              await refreshJournal();
            }
          }
          setView("customer");
        } else {
          setView("admin");
        }
      } catch (error) {
        setLoadError(error instanceof Error ? error.message : "Unable to start the session.");
      } finally {
        setBooted(true);
      }
    })();
  }, [iframeSession.status, iframeSession.sessionContext, loadCustomer, refreshJournal]);

  async function changeRole(role: string) {
    const next = await api.setRole(role);
    setSession(next);
    setTab("overview");
    setUsageLines(null);
    if (!next.perms.customer) {
      setCustomer(null);
      setView("admin");
    } else if (useMariaDemo) {
      await loadCustomer(MARIA_ID);
      setView((current) => (current === "admin" || current === "reports" && !next.perms.reports ? "customer" : current));
    } else {
      setCustomer(null);
      setSearchEmpty(false);
      setView((current) => (current === "admin" || current === "reports" && !next.perms.reports ? "customer" : current));
    }
    await refreshJournal();
  }

  async function handleSearch(event: FormEvent) {
    event.preventDefault();
    if (!session?.perms.customer) {
      setLoadError("IT administrators cannot search customer records.");
      setView("admin");
      return;
    }
    const result = await api.search(query);
    if (result.results[0]) {
      const loaded = await loadCustomer(result.results[0].id);
      setView("customer");
      if (loaded) {
        const event = await api.writeJournal("customer360.opened", loaded.id, { summary: "Search opened Customer 360" });
        setEvents((prev) => [event, ...prev]);
        toast(toastCopy("customer360.opened", event.detail));
      }
    } else {
      setSearchEmpty(true);
      setCustomer(null);
      setView("customer");
    }
  }

  async function confirmVerify(input: { method: "otp" | "pin" | "id"; code?: string; pin?: string }) {
    if (!customerId) return;
    const next = await api.verify({ customerId, ...input });
    setSession(next);
    setVerifyOpen(false);
    await loadCustomer(customerId);
    if (tab === "billing") {
      try {
        const data = await api.usage(customerId);
        setUsageLines(data.lines);
      } catch {
        setUsageLines(null);
      }
    }
    await refreshJournal();
    toast(toastCopy("cpni.verification.passed", { summary: `${next.cpni.status === "verified" ? next.cpni.method : input.method} - protected data unmasked for this session` }));
  }

  async function changeTab(next: Tab) {
    setTab(next);
    const labels: Record<Tab, string> = {
      overview: "Overview tab opened",
      billing: "Billing tab opened",
      services: "Services tab opened",
      interactions: "Interactions tab opened",
      tickets: "Tickets tab opened",
    };
    await journal("tab.viewed", labels[next], true);
    if (next === "billing" && customerId) {
      try {
        const data = await api.usage(customerId);
        setUsageLines(data.lines);
      } catch {
        setUsageLines(null);
      }
      await refreshJournal();
    }
  }

  function go(next: View) {
    if (next === "admin" && !session?.perms.admin) return;
    if (next === "reports" && !session?.perms.reports) return;
    if ((next === "customer" || next === "business") && !session?.perms.customer) {
      setView("admin");
      return;
    }
    setView(next);
    setMobileNav(false);
  }

  const nav = useMemo(
    () => [
      { id: "customer" as const, label: "Customer 360", icon: UserRound, show: Boolean(session?.perms.customer) },
      { id: "queue" as const, label: "My queue", icon: BookOpen, show: Boolean(session?.perms.customer) },
      { id: "reports" as const, label: "Team reports", icon: BarChart3, show: Boolean(session?.perms.reports) },
      { id: "admin" as const, label: "Admin console", icon: Settings, show: Boolean(session?.perms.admin) },
    ],
    [session],
  );

  if (iframeSession.status === "waitingHello") {
    return (
      <div className="grid h-full place-items-center text-[13px] text-muted-foreground">
        Connecting to OneAgent…
      </div>
    );
  }

  if (iframeSession.status === "denied") {
    return (
      <div className="grid h-full place-items-center px-6 text-center text-[13px] text-destructive">
        {loadError ?? "Embedded session refused."}
      </div>
    );
  }

  if (!booted || !session) {
    return (
      <div className="grid h-full place-items-center text-[13px] text-muted-foreground">
        {loadError ?? "Starting agent session…"}
      </div>
    );
  }

  const verified = session.cpni.status === "verified";

  return (
    <div
      ref={layoutRef}
      className="grid h-full grid-rows-[52px_1fr] md:grid-cols-[216px_1fr] md:grid-rows-[52px_1fr]"
    >
      <header className="z-20 flex items-center gap-3 border-b border-border bg-card px-3 md:col-span-2 md:px-4">
        <Button variant="ghost" size="icon-sm" className="md:hidden" onClick={() => setMobileNav(true)} aria-label="Open navigation">
          <Menu />
        </Button>
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <strong className="font-[family-name:var(--font-heading)] text-[15px] font-bold tracking-tight">Agent Desktop</strong>
            <span className="hidden text-[11.5px] text-muted-foreground sm:inline">Customer Account Console</span>
          </div>
        </div>
        <form onSubmit={handleSearch} className="relative mx-auto hidden max-w-[560px] flex-1 items-center gap-1.5 md:flex">
          <div className="relative flex-1">
            <Search className="absolute top-2 left-2.5 size-3.5 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, phone, account number, or email"
              aria-label="Search customers"
              className="h-8 border-[#dc2626] bg-secondary pl-8 ring-1 ring-[#dc2626]/40"
            />
          </div>
          <OneViewMark source="search" />
        </form>
        <div className="ml-auto flex items-center gap-2">
          <label className="hidden items-center gap-1.5 text-[12.5px] text-[#43535f] sm:flex">
            Role
            <select
              className="rounded-md border border-border bg-card px-2 py-1 text-[12.5px] font-semibold"
              value={session.role}
              onChange={(e) => void changeRole(e.target.value)}
            >
              {ROLE_OPTIONS.map((role) => (
                <option key={role.code} value={role.code}>{role.name}</option>
              ))}
            </select>
          </label>
          <Button variant="outline" size="sm" onClick={async () => { setDrawer(true); await journal("journal.opened", "Agent reviewed session journal", true); }}>
            <span className="mr-1 inline-block size-1.5 rounded-full bg-ok" />
            Journal <span className="num">{events.length}</span>
          </Button>
          <div className="flex items-center gap-2 text-[12.5px]">
            <span className="grid size-7 place-items-center rounded-full bg-primary text-[11px] font-bold text-white">{session.initials}</span>
            <span className="hidden sm:inline">{session.displayName}</span>
          </div>
        </div>
      </header>

      <nav className="hidden flex-col bg-rail px-2.5 py-3.5 text-rail-ink md:flex" aria-label="Main navigation">
        <div className="px-2.5 py-1 text-[10.5px] font-semibold tracking-[0.06em] text-[#7d8f9d]">WORKSPACE</div>
        {nav.filter((item) => item.show && (item.id === "customer" || item.id === "queue")).map((item) => (
          <NavButton key={item.id} active={view === item.id} label={item.label} icon={item.icon} onClick={() => go(item.id)} />
        ))}
        {session.perms.reports && (
          <>
            <div className="mt-2 px-2.5 py-1 text-[10.5px] font-semibold tracking-[0.06em] text-[#7d8f9d]">INSIGHTS</div>
            <NavButton active={view === "reports"} label="Team reports" icon={BarChart3} onClick={() => go("reports")} />
          </>
        )}
        {session.perms.admin && (
          <>
            <div className="mt-2 px-2.5 py-1 text-[10.5px] font-semibold tracking-[0.06em] text-[#7d8f9d]">SYSTEM</div>
            <NavButton active={view === "admin"} label="Admin console" icon={Settings} onClick={() => go("admin")} />
          </>
        )}
        <div className="mt-auto border-t border-[#22313e] px-2.5 pt-2.5 text-[11px] text-[#68798a]">
          Session journaled · Retention 7 yrs<br />
          Build 4.2.1 · TMF Open API v5
        </div>
      </nav>

      <main className="overflow-auto px-4 py-4 md:px-5">
        <form onSubmit={handleSearch} className="relative mb-3 md:hidden">
          <Search className="absolute top-2 left-2.5 size-3.5 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search customers" className="h-8 bg-card pl-8" />
        </form>
        <label className="mb-3 flex items-center gap-2 text-[12.5px] sm:hidden">
          Role
          <select className="rounded-md border border-border bg-card px-2 py-1 font-semibold" value={session.role} onChange={(e) => void changeRole(e.target.value)}>
            {ROLE_OPTIONS.map((role) => <option key={role.code} value={role.code}>{role.name}</option>)}
          </select>
        </label>

        {view === "customer" && !session.perms.customer && <ForbiddenCustomer />}
        {view === "customer" && session.perms.customer && searchEmpty && <EmptySearch query={query} mockHints={useMariaDemo} />}
        {view === "customer" && session.perms.customer && !searchEmpty && !customer && loadError && (
          <div className="rounded-[10px] border border-border bg-card px-4 py-8 text-center text-muted-foreground">{loadError}</div>
        )}
        {view === "customer" && session.perms.customer && !searchEmpty && !customer && !loadError && <IdleSearch />}
        {view === "customer" && session.perms.customer && customer && (
          <div className="mx-auto max-w-[1360px]">
            <OneViewLegend />
            <CustomerBand customer={customer} verified={verified} onVerify={() => setVerifyOpen(true)} onTicket={() => setTicketOpen(true)} />
            <VerifyBanner verified={verified} method={session.cpni.status === "verified" ? session.cpni.method : undefined} onVerify={() => setVerifyOpen(true)} />
            <div className="mt-4 grid items-start gap-4 xl:grid-cols-[1fr_320px]">
              <div>
                <CustomerTabs tab={tab} ticketCount={customer.tickets.length} onTab={(next) => void changeTab(next)} />
                {tab === "overview" && (
                  <OverviewTab
                    customer={customer}
                    onConsent={async () => {
                      const data = await api.consent(customer.id);
                      setConsentRows(data.versions);
                      setConsentOpen(true);
                      await refreshJournal();
                      toast(toastCopy("consent.viewed", { summary: "Service-data-for-offers consent history viewed" }));
                    }}
                  />
                )}
                {tab === "billing" && (
                  <BillingTab
                    customer={{
                      ...customer,
                      usage: verified ? usageLines : null,
                      usageStatus: verified ? "available" : "locked",
                    }}
                    perms={session.perms}
                    onInvoice={() => void journal("invoice.viewed", "Sep 2026 invoice")}
                    onCredit={() => setAdjustOpen(true)}
                    onPayLink={async () => { await api.payLink(customer.id); await refreshJournal(); toast(toastCopy("payment.link.sent", { summary: "Text to number on file - hosted pay page, card data never enters CRM" })); }}
                    onIvr={async () => { await api.ivrPay(customer.id); await refreshJournal(); toast(toastCopy("payment.ivr.transferred", { summary: "Automated phone payment - agent audio paused during entry" })); }}
                  />
                )}
                {tab === "services" && (
                  <ServicesTab
                    customer={customer}
                    perms={session.perms}
                    onAction={async (action, summary) => {
                      if (action === "diagnostics.run") {
                        const result = await api.diagnostics(customer.id);
                        await refreshJournal();
                        toast(toastCopy(action, { summary: result.result }));
                        return;
                      }
                      if (action === "device.action") {
                        await api.deviceAction(customer.id, summary);
                        await refreshJournal();
                        toast(toastCopy(action, { summary }));
                        return;
                      }
                      await journal(action, summary);
                    }}
                  />
                )}
                {tab === "interactions" && <InteractionsTab customer={customer} />}
                {tab === "tickets" && (
                  <TicketsTab
                    customer={customer}
                    onCreate={() => setTicketOpen(true)}
                    onOpen={(id) => void journal("ticket.viewed", `${id} viewed in Helix`)}
                  />
                )}
              </div>
              <InsightRail customer={customer} perms={session.perms} onAction={(action, summary) => void journal(action, summary)} onCredit={() => setAdjustOpen(true)} />
            </div>
          </div>
        )}
        {view === "queue" && (
          <QueueView
            onOpenCustomer={async () => {
              go("customer");
              if (useMariaDemo) await loadCustomer(MARIA_ID);
            }}
            onOpenBusiness={() => go("business")}
          />
        )}
        {view === "reports" && <ReportsView canApprove={session.role === "SUP"} onJournalRefresh={refreshJournal} />}
        {view === "business" && (
          <BusinessView
            perms={session.perms}
            onCustomer={() => go("customer")}
            onTicket={() => setTicketOpen(true)}
            onCredit={() => setAdjustOpen(true)}
            onAction={async (action, summary) => {
              if (action === "appointment.viewed" && customerId) {
                await api.appointments(customerId);
                await refreshJournal();
                toast(toastCopy(action, { summary }));
                return;
              }
              if (action === "appointment.offered" && customerId) {
                await api.offerAppointments(customerId);
                await refreshJournal();
                toast(toastCopy(action, { summary }));
                return;
              }
              await journal(action, summary);
            }}
          />
        )}
        {view === "admin" && <AdminView allowed={session.perms.admin || session.role === "SUP"} onAction={(action, summary) => void journal(action, summary)} />}
      </main>

      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="right" className="w-[380px] max-w-[380px] gap-0 p-0">
          <SheetHeader className="border-b border-border px-4 py-3">
            <SheetTitle>Interaction journal</SheetTitle>
          </SheetHeader>
          <div className="overflow-auto">
            {events.length === 0 && <p className="px-4 py-6 text-[13px] text-muted-foreground">No events in this session yet.</p>}
            {events.map((event) => (
              <div key={event.eventId} className="border-b border-[#e6ebf0] px-4 py-2.5 text-[12.5px]">
                <div className="text-[11px] text-muted-foreground num">{formatTime(event.eventTime)} · {event.actor.role}</div>
                <div className="font-semibold">{toastCopy(event.action, {}).split(" — ")[0]}</div>
                <div className="text-[#43535f]">{typeof event.detail.summary === "string" ? event.detail.summary : event.action}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
            Every action in this session is recorded with user, role, time, and context. Journals are tamper-evident and retained 7 years.
          </div>
        </SheetContent>
      </Sheet>

      {mobileNav && (
        <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setMobileNav(false)}>
          <div className="flex h-full w-[216px] flex-col bg-rail p-3 text-rail-ink" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between px-2 text-white">
              <span className="font-semibold">Menu</span>
              <Button variant="ghost" size="icon-xs" onClick={() => setMobileNav(false)} aria-label="Close navigation"><X className="text-white" /></Button>
            </div>
            {nav.filter((item) => item.show).map((item) => (
              <NavButton key={item.id} active={view === item.id} label={item.label} icon={item.icon} onClick={() => go(item.id)} />
            ))}
          </div>
        </div>
      )}

      <VerifyModal
        open={verifyOpen}
        onOpenChange={setVerifyOpen}
        demoOtp={useMariaDemo}
        mobileHint={customer?.contact.mobile.replace(/\D/g, "").slice(-4) || undefined}
        onSendOtp={async () => {
          if (!customerId) return { hint: "the mobile number on file" };
          return api.verifyChallenge(customerId);
        }}
        onConfirm={(input) => confirmVerify(input)}
      />
      {perms && (
        <AdjustModal
          open={adjustOpen}
          onOpenChange={setAdjustOpen}
          perms={perms}
          onSubmit={async (input) => {
            setAdjustOpen(false);
            if (!customerId) return;
            const result = await api.credit({ customerId, ...input });
            await refreshJournal();
            toast(toastCopy(result.status === "pendingApproval" ? "credit.approvalRequested" : "credit.applied", {
              summary: result.status === "pendingApproval"
                ? `$${input.amount.toFixed(2)} exceeds your $${perms.adjustLimit} limit - routed to Supervisor`
                : `$${input.amount.toFixed(2)} - ${input.reason} - posts to next invoice`,
            }));
          }}
        />
      )}
      <TicketModal
        open={ticketOpen}
        onOpenChange={setTicketOpen}
        onSubmit={async (input) => {
          setTicketOpen(false);
          if (session.perms.customer && customerId) {
            await api.createTicket(customerId, input);
            await loadCustomer(customerId);
          }
          await refreshJournal();
          toast("Helix ticket created");
        }}
      />
      <Dialog open={consentOpen} onOpenChange={setConsentOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Consent history</DialogTitle>
            <DialogDescription>Service-data-for-offers agreement versions for this account. No usage-derived offers without the current opt-in.</DialogDescription>
          </DialogHeader>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] text-muted-foreground">
                <th className="py-1">Effective</th>
                <th className="py-1">Channel</th>
                <th className="py-1">Service data for offers</th>
              </tr>
            </thead>
            <tbody>
              {consentRows.map((row) => (
                <tr key={row.effective} className="border-t border-border">
                  <td className="py-2">{row.effective}</td>
                  <td className="py-2">{row.channel}</td>
                  <td className="py-2">{row.serviceDataForOffers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NavButton({
  active,
  label,
  icon: Icon,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] ${active ? "bg-[#1f3040] font-semibold text-white" : "hover:bg-[#1f3040]"}`}
    >
      <Icon className="size-4 opacity-85" />
      {label}
    </button>
  );
}
